import { useState, useEffect, useMemo, useRef } from 'react';
import { Plus, Search, X, Loader2, Moon, Sun, Menu } from 'lucide-react';
import ApplianceCard from '@/components/appliances/ApplianceCard';
import ServiceRecordForm from '@/components/appliances/ServiceRecordForm';
import RecallModal from '@/components/appliances/RecallModal';
import { useAppliances } from '@/contexts/ApplianceContext';
import { useAiAction } from '@/hooks/useAiAction';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { AppState, AppAction, ApplianceCategory, Appliance, ApplianceRecall, Role } from '@/types';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';

interface ApplianceFormState {
  name: string;
  make: string;
  model: string;
  category?: ApplianceCategory;
  modelNumber?: string;
  purchaseDate?: string;
  purchasePrice?: number;
  warrantyExpires?: string;
  manualUrl?: string;
  photoFile?: File;
}

const INITIAL_FORM_STATE: ApplianceFormState = {
  name: '',
  make: '',
  model: '',
  category: undefined,
  modelNumber: '',
  purchaseDate: '',
  purchasePrice: undefined,
  warrantyExpires: '',
  manualUrl: '',
  photoFile: undefined,
};

export function Appliances({ state, dispatch }: { state: AppState; dispatch: React.Dispatch<AppAction> }) {
  const { appliances, loading, householdId, addAppliance, updateAppliance, deleteAppliance, predictMaintenance, getRecalls, getServiceRecords } = useAppliances();
  const { callAction: callAiAction } = useAiAction(householdId);
  
  const [selectedCategory, setSelectedCategory] = useState<ApplianceCategory | 'all'>('all');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedAppliance, setSelectedAppliance] = useState<string | null>(null);
  const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false);
  
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [formState, setFormState] = useState<ApplianceFormState>(INITIAL_FORM_STATE);
  const [isManualFormVisible, setIsManualFormVisible] = useState(false);
  
  const [aiPrediction, setAiPrediction] = useState<{ predictedNextServiceDate: string; riskScore: string; reasoning: string } | null>(null);
  const [aiPredictionLoading, setAiPredictionLoading] = useState(false);
  const [aiPredictionError, setAiPredictionError] = useState<string | null>(null);
  
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [recallRecords, setRecallRecords] = useState<ApplianceRecall[]>([]);
  const [selectedRecall, setSelectedRecall] = useState<ApplianceRecall | null>(null);
  const [hasRecalls, setHasRecalls] = useState(false);

  // Refs for focus management
  const modalRef = useRef<HTMLDivElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);

  // Focus trap helpers
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setIsAddModalOpen(false);
        setIsDetailDrawerOpen(false);
        setSelectedRecall(null);
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const appliancesByCategory = useMemo(() => {
    return appliances.filter(
      a => selectedCategory === 'all' || a.category === selectedCategory
    );
  }, [appliances, selectedCategory]);

  const handleLookupModel = async () => {
    if (!formState.modelNumber?.trim()) return;
    
    setLookupLoading(true);
    setLookupError(null);
    try {
      const fn = httpsCallable(getFunctions(), 'lookupApplianceModel');
      const result = await fn({ modelNumber: formState.modelNumber });
      
      const resultData = result.data as any;
      if (resultData && resultData.found) {
        setFormState(prev => ({
          ...prev,
          make: resultData.make || prev.make,
          model: resultData.model || prev.model,
          category: resultData.category as ApplianceCategory || prev.category,
          manualUrl: resultData.manualUrl || prev.manualUrl,
        }));
        setIsManualFormVisible(false);
      } else {
        setIsManualFormVisible(true);
        setLookupError('No model data found. Please fill in manually.');
      }

    } catch (error) {
      setIsManualFormVisible(true);
      setLookupError('Lookup failed. Please fill in manually.');
      console.error('Lookup error:', error);
    } finally {
      setLookupLoading(false);
    }
  };

  const handlePhotoUpload = async (file: File) => {
    if (!householdId || !file) return;
    
    try {
      const storage = getStorage();
      const fileRef = ref(storage, `households/${householdId}/appliances/${Date.now()}/${file.name}`);
      const snapshot = await uploadBytes(fileRef, file);
      const downloadUrl = await getDownloadURL(snapshot.ref);
      return downloadUrl;
    } catch (error) {
      console.error('Photo upload failed:', error);
      return null;
    }
  };

  const handleSubmitAppliance = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!householdId) return;

    let photoUrl = formState.photoFile ? await handlePhotoUpload(formState.photoFile) : undefined;

    try {
      await addAppliance({
        name: formState.name,
        make: formState.make,
        model: formState.model,
        modelNumber: formState.modelNumber || undefined,
        category: formState.category || undefined,
        purchaseDate: formState.purchaseDate || undefined,
        purchasePrice: formState.purchasePrice ? Number(formState.purchasePrice) : undefined,
        warrantyExpiresAt: formState.warrantyExpires ? `${formState.warrantyExpires}T00:00:00.000Z` : undefined,
        manualUrl: formState.manualUrl || undefined,
        photoUrl: photoUrl ?? undefined,
        householdId,
        addedBy: state.currentUser?.id || 'unknown',
      });
      setIsAddModalOpen(false);
      setFormState(INITIAL_FORM_STATE);
    } catch (error) {
      console.error('Error adding appliance:', error);
      alert('Failed to add appliance. Please try again.');
    }
  };

  const handleDeleteAppliance = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this appliance?')) {
      try {
        await deleteAppliance(id);
        setIsDetailDrawerOpen(false);
      } catch (error) {
        console.error('Error deleting appliance:', error);
        alert('Failed to delete appliance. Please try again.');
      }
    }
  };

  const handleGetPrediction = async (appliance: Appliance) => {
    setAiPredictionLoading(true);
    setAiPredictionError(null);
    try {
      const history = await getServiceRecords(appliance.id);
      const prediction = await predictMaintenance(appliance, history);
      setAiPrediction(prediction);
    } catch (error) {
      setAiPredictionError('Failed to generate prediction. Please try again.');
      console.error('Prediction error:', error);
    } finally {
      setAiPredictionLoading(false);
    }
  };

  const openApplianceDetails = (applianceId: string) => {
    setSelectedAppliance(applianceId);
    setIsDetailDrawerOpen(true);
    setHasRecalls(false);
    
    // Preload recalls
    const appliance = appliances.find(a => a.id === applianceId);
    if (appliance) {
      getRecalls(applianceId).then(recalls => {
        setRecallRecords(recalls);
        setHasRecalls(recalls.length > 0);
      }).catch(() => {});
    }
  };

  const renderRiskBadge = (score?: string) => {
    switch (score) {
      case 'high': return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">High</span>;
      case 'medium': return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">Medium</span>;
      case 'low': return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">Low</span>;
      default: return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300">Unknown</span>;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-semibold text-slate-900 dark:text-white">Appliance Intelligence</h1>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => dispatch({ type: 'TOGGLE_THEME' })}
                className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
              >
                {state.theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-wrap gap-2">
          {['all', 'kitchen', 'laundry', 'hvac', 'plumbing', 'electrical', 'other'].map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category as ApplianceCategory | 'all')}
              className={`px-3 py-1.5 rounded-full text-sm font-medium capitalize transition-colors ${
                selectedCategory === category
                  ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900'
                  : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading && (
            <div className="col-span-full flex justify-center py-12">
              <LoadingSpinner />
            </div>
          )}

          {(!loading && appliancesByCategory.length === 0) && (
            <div className="col-span-full py-12 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 mb-4">
                <Search className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-medium text-slate-900 dark:text-white">No appliances found</h3>
              <p className="mt-2 text-slate-600 dark:text-slate-400">
                {selectedCategory === 'all'
                  ? "Get started by adding your first appliance to track"
                  : `No appliances in the ${selectedCategory} category`}
              </p>
              <button
                onClick={() => setIsAddModalOpen(true)}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-slate-900 hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Appliance
              </button>
            </div>
          )}

          {appliancesByCategory.map((appliance) => (
            <ApplianceCard
              key={appliance.id}
              appliance={appliance}
              riskScore={appliance.recallStatus === 'recalled' ? 'high' : (appliance.recallStatus === 'unknown' ? 'medium' : 'low')}
              recallCount={0}
              onClick={() => openApplianceDetails(appliance.id)}
            />
          ))}
        </div>
      </main>

      {/* FAB */}
      <button
        onClick={() => setIsAddModalOpen(true)}
        className="fixed bottom-6 right-6 p-4 rounded-full bg-slate-900 text-white hover:bg-slate-800 shadow-lg dark:bg-white dark:text-slate-900 transition-all hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-900 dark:focus:ring-offset-slate-950 z-40"
        aria-label="Add appliance"
      >
        <Plus className="w-6 h-6" />
      </button>

      {/* Add Appliance Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div 
              className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity" 
              aria-hidden="true"
            />
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div 
              className="inline-block align-bottom bg-white dark:bg-slate-900 rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full"
              ref={modalRef}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800">
                <h3 className="text-lg font-medium leading-6 text-slate-900 dark:text-white" id="modal-title">
                  Add New Appliance
                </h3>
                <button 
                  onClick={() => setIsAddModalOpen(false)}
                  className="text-slate-400 hover:text-slate-500 dark:hover:text-slate-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmitAppliance}>
                <div className="px-6 py-5 space-y-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                      Model Number
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={formState.modelNumber}
                        onChange={(e) => setFormState(prev => ({ ...prev, modelNumber: e.target.value }))}
                        placeholder="Enter model number (e.g., ABC-1234)"
                        className="flex-1 block w-full rounded-lg border-slate-300 shadow-sm focus:ring-slate-500 focus:border-slate-500 sm:text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                        disabled={lookupLoading}
                      />
                      <button
                        type="button"
                        onClick={handleLookupModel}
                        disabled={!formState.modelNumber || lookupLoading}
                        className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 dark:bg-white dark:text-slate-900 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {lookupLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Look up'}
                      </button>
                    </div>
                    {lookupError && (
                      <p className="text-sm text-red-600 dark:text-red-400">{lookupError}</p>
                    )}
                  </div>

                  {!isManualFormVisible && !lookupError && (
                    <div className="flex justify-center">
                      <p className="text-sm text-slate-500 dark:text-slate-400 animate-pulse">
                        Pre-filling from manufacturer database...
                      </p>
                    </div>
                  )}

                  {isManualFormVisible && (
                    <>
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                          Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={formState.name}
                          onChange={(e) => setFormState(prev => ({ ...prev, name: e.target.value }))}
                          className="block w-full rounded-lg border-slate-300 shadow-sm focus:ring-slate-500 focus:border-slate-500 sm:text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                          Make <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={formState.make}
                          onChange={(e) => setFormState(prev => ({ ...prev, make: e.target.value }))}
                          className="block w-full rounded-lg border-slate-300 shadow-sm focus:ring-slate-500 focus:border-slate-500 sm:text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                          Model <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={formState.model}
                          onChange={(e) => setFormState(prev => ({ ...prev, model: e.target.value }))}
                          className="block w-full rounded-lg border-slate-300 shadow-sm focus:ring-slate-500 focus:border-slate-500 sm:text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                          Category
                        </label>
                        <select
                          value={formState.category || ''}
                          onChange={(e) => setFormState(prev => ({ ...prev, category: e.target.value as ApplianceCategory }))}
                          className="block w-full rounded-lg border-slate-300 shadow-sm focus:ring-slate-500 focus:border-slate-500 sm:text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                        >
                          <option value="">Select category</option>
                          <option value="kitchen">Kitchen</option>
                          <option value="laundry">Laundry</option>
                          <option value="hvac">HVAC</option>
                          <option value="plumbing">Plumbing</option>
                          <option value="electrical">Electrical</option>
                          <option value="other">Other</option>
                        </select>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                            Purchase Date
                          </label>
                          <input
                            type="date"
                            value={formState.purchaseDate || ''}
                            onChange={(e) => setFormState(prev => ({ ...prev, purchaseDate: e.target.value }))}
                            className="block w-full rounded-lg border-slate-300 shadow-sm focus:ring-slate-500 focus:border-slate-500 sm:text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                          />
                        </div>

                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                            Purchase Price
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={formState.purchasePrice || ''}
                            onChange={(e) => setFormState(prev => ({ ...prev, purchasePrice: Number(e.target.value) }))}
                            placeholder="0.00"
                            className="block w-full rounded-lg border-slate-300 shadow-sm focus:ring-slate-500 focus:border-slate-500 sm:text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                          Warranty Expires
                        </label>
                        <input
                          type="date"
                          value={formState.warrantyExpires || ''}
                          onChange={(e) => setFormState(prev => ({ ...prev, warrantyExpires: e.target.value }))}
                          className="block w-full rounded-lg border-slate-300 shadow-sm focus:ring-slate-500 focus:border-slate-500 sm:text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                          Manual URL
                        </label>
                        <input
                          type="url"
                          value={formState.manualUrl || ''}
                          onChange={(e) => setFormState(prev => ({ ...prev, manualUrl: e.target.value }))}
                          placeholder="https://example.com/manual.pdf"
                          className="block w-full rounded-lg border-slate-300 shadow-sm focus:ring-slate-500 focus:border-slate-500 sm:text-sm dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                          Appliance Photo
                        </label>
                        <div className="flex items-center justify-center w-full">
                          <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-slate-300 border-dashed rounded-lg cursor-pointer bg-slate-50 hover:bg-slate-100 dark:bg-slate-800 dark:border-slate-600 dark:hover:bg-slate-700">
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                              <Plus className="w-6 h-6 mb-2 text-slate-400" />
                              <p className="mb-2 text-sm text-slate-500 dark:text-slate-400">
                                <span className="font-semibold">Click to upload</span> or drag and drop
                              </p>
                              <p className="text-xs text-slate-500 dark:text-slate-400">SVG, PNG, JPG or GIF (MAX. 800x400px)</p>
                            </div>
                            <input 
                              type="file" 
                              className="hidden" 
                              accept="image/*"
                              onChange={(e) => { const f = e.target.files?.[0]; if (f) setFormState(prev => ({ ...prev, photoFile: f })); }}
                            />
                          </label>
                        </div>
                        {formState.photoFile && (
                          <p className="text-xs text-green-600 dark:text-green-400">Selected: {formState.photoFile.name}</p>
                        )}
                      </div>
                    </>
                  )}
                </div>

                <div className="bg-slate-50 dark:bg-slate-800/50 px-6 py-4 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setIsAddModalOpen(false);
                      setFormState(INITIAL_FORM_STATE);
                    }}
                    className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={lookupLoading}
                    className="px-4 py-2 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-900 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200 disabled:opacity-50"
                  >
                    {lookupLoading ? 'Looking up...' : 'Add Appliance'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Detail Drawer */}
      {isDetailDrawerOpen && selectedAppliance && (() => {
        const appliance = appliances.find(a => a.id === selectedAppliance);
        if (!appliance) return null;
        
        return (
          <div className="fixed inset-0 z-50 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div 
                className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity" 
                onClick={() => setIsDetailDrawerOpen(false)}
                aria-hidden="true"
              />
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                <div 
                  className="pointer-events-auto w-screen max-w-md"
                  ref={drawerRef}
                >
                  <div className="h-full flex flex-col bg-white dark:bg-slate-900 shadow-xl">
                    <div className="flex items-start justify-between p-6 border-b border-slate-200 dark:border-slate-800">
                      <div>
                        <h2 className="text-lg font-medium text-slate-900 dark:text-white">
                          {appliance.name}
                        </h2>
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                          {appliance.make} {appliance.model} {appliance.modelNumber && `(${appliance.modelNumber})`}
                        </p>
                      </div>
                      <button
                        onClick={() => setIsDetailDrawerOpen(false)}
                        className="text-slate-400 hover:text-slate-500 dark:hover:text-slate-300"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-6 space-y-6">
                      {/* AI Prediction Section */}
                      <section>
                        <h3 className="text-sm font-medium text-slate-900 dark:text-white mb-3">
                          AI Maintenance Prediction
                        </h3>
                        <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
                          <button
                            type="button"
                            onClick={() => handleGetPrediction(appliance)}
                            disabled={aiPredictionLoading || !householdId}
                            className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {aiPredictionLoading ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Generating prediction...
                              </>
                            ) : (
                              <>
                                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                </svg>
                                Get AI Prediction
                              </>
                            )}
                          </button>
                          
                          {aiPredictionError && (
                            <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                              {aiPredictionError}
                            </p>
                          )}
                          
                          {aiPrediction && (
                            <div className="mt-4 space-y-2">
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-slate-700 dark:text-slate-300">Next Recommended Service:</span>
                                <span className="text-sm font-semibold text-slate-900 dark:text-white">
                                  {new Date(aiPrediction.predictedNextServiceDate).toLocaleDateString()}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-slate-700 dark:text-slate-300">Risk Level:</span>
                                {renderRiskBadge(aiPrediction.riskScore)}
                              </div>
                              <div>
                                <p className="text-sm text-slate-700 dark:text-slate-300">Reasoning:</p>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                                  {aiPrediction.reasoning}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </section>

                      {/* Service Records */}
                      <section>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-medium text-slate-900 dark:text-white">
                            Service History
                          </h3>
                          <button
                            type="button"
                            onClick={() => setShowServiceForm(!showServiceForm)}
                            className="text-sm text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                          >
                            {showServiceForm ? 'Cancel' : 'Add Record'}
                          </button>
                        </div>
                        
                        {showServiceForm ? (
                          <div className="mb-4">
                            <ServiceRecordForm
                              applianceId={appliance.id}
                              householdId={householdId || ''}
                              onSave={async () => {
                                setShowServiceForm(false);
                                setAiPrediction(null); // Clear prediction to refresh
                              }}
                              onCancel={() => setShowServiceForm(false)}
                            />
                          </div>
                        ) : (
                          <>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">
                              No service records yet.
                            </p>
                          </>
                        )}
                      </section>

                      {/* Recalls */}
                      <section>
                        <h3 className="text-sm font-medium text-slate-900 dark:text-white mb-3">
                          Recalls
                        </h3>
                        
                        {hasRecalls ? (
                          <>
                            <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 mb-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                  <div className="h-8 w-8 rounded-full bg-red-100 dark:bg-red-800 flex items-center justify-center">
                                    <svg className="h-5 w-5 text-red-600 dark:text-red-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                  </div>
                                  <div className="ml-3">
                                    <p className="text-sm font-medium text-red-800 dark:text-red-200">
                                      {recallRecords.length} {recallRecords.length === 1 ? 'recall' : 'recalls'} detected for this appliance
                                    </p>
                                    <p className="text-xs text-red-600 dark:text-red-300 mt-1">
                                      Review safety information and contact the manufacturer
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            <button
                              type="button"
                              onClick={() => { setHasRecalls(true); setSelectedRecall(recallRecords[0]); }}
                              className="w-full text-sm text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 text-left"
                            >
                              View recall details
                            </button>
                          </>
                        ) : (
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            No recalls detected for this appliance.
                          </p>
                        )}
                      </section>

                      {/* Admin Actions */}
                      {state.currentUser?.role === Role.ADMIN && (
                        <section className="border-t border-slate-200 dark:border-slate-800 pt-6">
                          <button
                            type="button"
                            onClick={() => handleDeleteAppliance(appliance.id)}
                            className="flex items-center text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          >
                            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete Appliance
                          </button>
                        </section>
                      )}
                    </div>

                    <div className="bg-slate-50 dark:bg-slate-800/50 px-6 py-4 flex justify-end">
                      <button
                        type="button"
                        onClick={() => setIsDetailDrawerOpen(false)}
                        className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Recall Modal */}
      {selectedRecall && (
        <RecallModal
          recall={selectedRecall}
          applianceName={appliances.find(a => a.id === selectedAppliance)?.name || 'Unknown Appliance'}
          onClose={() => setSelectedRecall(null)}
        />
      )}
    </div>
  );
}
